import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryScatter } from 'victory';
import DashboardService from 'api/dashboard/DashboardService';
import { ContractMonthlyEvolutionDashboardDto } from 'api/dashboard/dtos/ContractMonthlyEvolutionDashboardDto';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import Utils from 'common/services/Utils';
import dayjs from 'dayjs';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ContractsEvolution.module.scss';

const LINE_COLOR = '#02c7c7';
const AREA_COLOR = 'rgba(2, 199, 199, 0.15)';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const CONTRACTS_FILTERS_CACHE_KEY = 'contracts-listing-filters';

const ContractsEvolution = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ContractMonthlyEvolutionDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('contractsEvolution');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const [tooltip, setTooltip] = useState<{ label: string; value: number; x: number; y: number } | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getContractsEvolution(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const chartData = useMemo(() => {
		if (!data) return [];
		return data.map(item => ({
			date: dayjs(item.date).format('YYYY-MM'),
			value: item.totalCost,
		}));
	}, [data]);

	const periodTotal = (data || []).reduce((sum, item) => sum + (item.totalCost || 0), 0);

	const MAX_TICKS = 12;
	const TICK_STEPS = [1, 2, 3, 6, 12];
	const tickStep = TICK_STEPS.find(s => Math.ceil(chartData.length / s) <= MAX_TICKS) ?? 12;

	const navigateToContracts = () => {
		const fromSource = props.filters?.dateFrom ?? dateFrom;
		const toSource = props.filters?.dateTo ?? dateTo;
		// showOnlyLast off para a listagem reflectir todos os contratos contados no widget
		// (caso contrário seguros/manutenções sobrepostos por veículo ficam ocultos).
		const filters = {
			contractDateFrom: dayjs(fromSource).startOf('month').toDate(),
			contractDateTo: dayjs(toSource).endOf('month').toDate(),
			showOnlyLast: false,
			// vehiclesInactive: false — dashboard agrega só viaturas ativas; clique mantém o critério.
			vehiclesInactive: false,
		};
		try {
			localStorage.setItem(CONTRACTS_FILTERS_CACHE_KEY, JSON.stringify(filters));
		} catch { /* silent */ }
		window.open('/contracts', '_blank');
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.contracts_evolution') as string}
			subTitleTooltip={t('dashboard.contracts_evolution_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<KpiHeader
				value={Utils.formatCurrency(periodTotal)}
				subLabel={t('dashboard.period_total' as any) as string}
				currentTotal={periodTotal}
				previousTotal={null}
				previousRange={null}
				onClick={navigateToContracts}
			/>
			<div
				className={styles.chartContainer}
				ref={containerRef}
				onClick={navigateToContracts}
				role="button"
				tabIndex={0}
				onKeyDown={e => { if (e.key === 'Enter') navigateToContracts(); }}
			>
				{isLoading ? (
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				) : chartData.length === 0 ? (
					<WidgetNoData />
				) : (
					width > 0 && height > 0 && (
						<VictoryChart
							width={width}
							height={height}
							domainPadding={{ x: Math.max(20, 40 - chartData.length * 2) }}
							padding={{ top: 20, bottom: 50, left: 70, right: 20 }}
						>
							<VictoryAxis
								style={{
									axis: { stroke: 'transparent' },
									tickLabels: { fill: '#000000', userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
								}}
								tickFormat={(x, i) => i % tickStep === 0 ? dayjs(x + '-01').format('MMM YY') : ''}
							/>
							<VictoryAxis
								dependentAxis
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								tickFormat={x => Utils.formatCurrency(x)}
							/>
							<VictoryArea
								data={chartData}
								x="date"
								y="value"
								interpolation="monotoneX"
								style={{
									data: {
										fill: AREA_COLOR,
										stroke: LINE_COLOR,
										strokeWidth: 2,
									},
								}}
							/>
							<VictoryScatter
								data={chartData}
								x="date"
								y="value"
								size={4}
								style={{
									data: {
										fill: LINE_COLOR,
										stroke: '#fff',
										strokeWidth: 1.5,
										cursor: 'pointer',
									},
								}}
								// eslint-disable-next-line
								events={[{
									target: 'data' as const,
									eventHandlers: {
										onMouseEnter: (evt: any, targetProps: any) => {
											const datum = targetProps.datum;
											setTooltip({
												label: dayjs(datum.date + '-01').format('MMM YYYY'),
												value: datum.value,
												x: evt.clientX,
												y: evt.clientY,
											});
											return [];
										},
										onMouseMove: (evt: any) => {
											setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
											return [];
										},
										onMouseLeave: () => { setTooltip(null); return []; },
									},
								}]}
							/>
						</VictoryChart>
					)
				)}
				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
						<div className={styles.tooltipTitle}>{tooltip.label}</div>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: LINE_COLOR }} />
							<span className={styles.tooltipRowLabel}>{t('dashboard.contracts_evolution_cost_label' as any)}:</span>
							<span className={styles.tooltipRowValue}>{Utils.formatCurrency(tooltip.value)}</span>
						</div>
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default ContractsEvolution;
